import type { SanityProductsNewData, SanityProductsNewProduct, SanityProductsNewRev, SanityRichTextValue } from "@ouster/shared";
import type { DownloadCell, DownloadGroupedCell, DownloadRow, DownloadTableKey, DownloadTooltipView, DownloadVersionOption, DownloadWarningView, DownloadsView } from "~/features/downloads/adapter";

/**
 * Adapt the Downloads schema (productRev + productFirmware + productGuide +
 * productAccessory + generalDownloadSection docs) into the DownloadsView
 * consumed by the `productDownloadsTable` pageBuilder section.
 *
 * Cell scoping: `hardwareSlugs` restricts a cell to specific rev picks,
 * `firmwareSlugs` to specific firmware picks. An empty array means the cell
 * applies to any pick on that axis.
 */

// Product column order in the hardware table. Matches the legacy render.
const PRODUCT_COLUMN_ORDER = ["OSDome", "OS0", "OS1", "OS1 Max", "OS2"] as const;
function linkText(url: string): string {
  const lower = url.toLowerCase();
  if (lower.endsWith(".pdf")) return "PDF";
  if (lower.endsWith(".zip")) return "ZIP";
  if (lower.endsWith(".exe")) return "EXE";
  if (lower.endsWith(".dmg")) return "DMG";
  if (lower.endsWith(".appimage")) return "AppImage";
  if (lower.endsWith(".img")) return "IMG";
  return "LINK";
}
function linkCell(url: string, hardwareSlugs: string[], firmwareSlugs: string[], overrideText?: string): DownloadCell {
  return {
    kind: "link",
    href: url,
    text: overrideText ?? linkText(url),
    newWindow: true,
    download: linkText(url) !== "LINK",
    hardwareSlugs,
    firmwareSlugs
  };
}
function textCell(text: string, hardwareSlugs: string[], firmwareSlugs: string[]): DownloadCell {
  return {
    kind: "text",
    text,
    hardwareSlugs,
    firmwareSlugs
  };
}
function comingSoonCell(hardwareSlugs: string[], firmwareSlugs: string[]): DownloadCell {
  return textCell("Coming Soon", hardwareSlugs, firmwareSlugs);
}
function productKey(name: string): number {
  const idx = PRODUCT_COLUMN_ORDER.indexOf(name as (typeof PRODUCT_COLUMN_ORDER)[number]);
  return idx === -1 ? PRODUCT_COLUMN_ORDER.length : idx;
}

// Collect every product name used across all revs, sorted by our canonical
// column order (unknown names go to the end preserving insertion order).
function collectProductColumns(revs: SanityProductsNewRev[]): string[] {
  const seen = new Set<string>();
  for (const rev of revs) {
    for (const p of rev.products ?? []) {
      if (p.product) seen.add(p.product);
    }
  }
  return Array.from(seen).sort((a, b) => productKey(a) - productKey(b));
}

// Build a "hardware axis" row (Part Numbers / Datasheet / etc.). For each
// product column and each rev that has that product, emit whatever cell the
// row-builder returns, scoped by the rev's slug.
function buildHardwareRow(heading: string, revs: SanityProductsNewRev[], productColumns: string[], cellsFor: (rev: SanityProductsNewRev, product: SanityProductsNewProduct, revSlug: string) => DownloadCell[]): DownloadRow {
  const groupedCells: DownloadGroupedCell[] = productColumns.map(productName => {
    const content: DownloadCell[] = [];
    for (const rev of revs) {
      const revSlug = rev.slug ?? "";
      if (!revSlug) continue;
      const product = (rev.products ?? []).find(p => p.product === productName);
      if (!product) continue;
      content.push(...cellsFor(rev, product, revSlug));
    }
    return {
      heading: productName,
      content
    };
  }).filter(g => g.content.length > 0);
  return {
    heading,
    groupedCells,
    fullWidthCells: []
  };
}
function adaptRevs(revs: SanityProductsNewRev[]): DownloadRow[] {
  const productColumns = collectProductColumns(revs);
  if (!productColumns.length) return [];
  const partNumbers = buildHardwareRow("Part Numbers", revs, productColumns, (_rev, product, revSlug) => product.partNumber ? [textCell(product.partNumber, [revSlug], [])] : []);
  const datasheets = buildHardwareRow("Datasheet", revs, productColumns, (_rev, product, revSlug) => (product.datasheets ?? []).map(ds => {
    const fwSlug = ds.firmware?.slug;
    if (!ds.pdf || !fwSlug) return null;
    return linkCell(ds.pdf, [revSlug], [fwSlug]);
  }).filter((cell): cell is DownloadCell => Boolean(cell)));
  const hardwareUserManual = buildHardwareRow("Hardware User Manual", revs, productColumns, (_rev, product, revSlug) => {
    const urls = product.hardwareUserManual ?? [];
    if (urls.length === 0) return [comingSoonCell([revSlug], [])];
    return urls.map(url => linkCell(url, [revSlug], []));
  });
  const sensorCad = buildHardwareRow("Sensor CAD Models", revs, productColumns, (_rev, product, revSlug) => product.sensorCadZip ? [linkCell(product.sensorCadZip, [revSlug], [])] : []);
  return [partNumbers, datasheets, hardwareUserManual, sensorCad].filter(row => row.groupedCells.length > 0);
}
function adaptAccessoriesTable(data: SanityProductsNewData): DownloadRow[] {
  const revs = data.revs ?? [];
  const productColumns = collectProductColumns(revs);

  // Accessory CAD Models — grouped per product, filtered by rev slug.
  const accessoryCad = buildHardwareRow("Accessory CAD Models", revs, productColumns, (_rev, product, revSlug) => product.accessoryCadZip ? [linkCell(product.accessoryCadZip, [revSlug], [])] : []);

  // For each unique accessory referenced by any rev, emit a full-width row.
  const seen = new Set<string>();
  const accessoryRows: DownloadRow[] = [];
  for (const rev of revs) {
    for (const product of rev.products ?? []) {
      for (const acc of product.hardwareAccessories ?? []) {
        if (!acc._id || seen.has(acc._id)) continue;
        seen.add(acc._id);
        if (!acc.pdf) continue;
        accessoryRows.push({
          heading: acc.title ?? "Accessory",
          groupedCells: [],
          fullWidthCells: [linkCell(acc.pdf, [], [])]
        });
      }
    }
  }
  const out: DownloadRow[] = [];
  if (accessoryCad.groupedCells.length) out.push(accessoryCad);
  out.push(...accessoryRows);
  return out;
}
function adaptFirmwareTable(data: SanityProductsNewData): DownloadRow[] {
  const firmwares = data.firmwares ?? [];
  const buildFirmwareRow = (heading: string, cellsFor: (fw: (typeof firmwares)[number]) => DownloadCell[]): DownloadRow => {
    const fullWidthCells: DownloadCell[] = [];
    for (const fw of firmwares) {
      if (!fw.slug) continue;
      const cells = cellsFor(fw);
      if (cells.length === 0) fullWidthCells.push(comingSoonCell([], [fw.slug]));else fullWidthCells.push(...cells);
    }
    return {
      heading,
      groupedCells: [],
      fullWidthCells
    };
  };
  const image = buildFirmwareRow("Firmware Image", fw => fw.image ? [linkCell(fw.image, [], [fw.slug!])] : []);
  const sha = buildFirmwareRow("SHA256", fw => fw.sha256 ? [textCell(fw.sha256, [], [fw.slug!])] : []);
  const userManual = buildFirmwareRow("Firmware User Manual", fw => (fw.userManual ?? []).map(url => linkCell(url, [], [fw.slug!])));
  const changelog = buildFirmwareRow("Changelog", fw => (fw.changelog ?? []).map(url => linkCell(url, [], [fw.slug!])));
  return [image, sha, userManual, changelog].filter(row => row.fullWidthCells.length > 0);
}
function adaptGuidesTable(data: SanityProductsNewData): DownloadRow[] {
  const guides = data.guides ?? [];
  const firmwares = data.firmwares ?? [];
  const rows: DownloadRow[] = [];
  for (const guide of guides) {
    const title = guide.title ?? "";
    const fullWidthCells: DownloadCell[] = [];

    // Special case: the API Guide row is fed by each firmware's apiGuide URLs.
    if (title.toLowerCase().includes("api guide")) {
      for (const fw of firmwares) {
        if (!fw.slug) continue;
        const urls = fw.apiGuide ?? [];
        if (urls.length === 0) {
          fullWidthCells.push(comingSoonCell([], [fw.slug]));
        } else {
          for (const url of urls) fullWidthCells.push(linkCell(url, [], [fw.slug]));
        }
      }
    } else {
      const urls = guide.urls ?? [];
      if (urls.length === 0) fullWidthCells.push(comingSoonCell([], []));else for (const url of urls) fullWidthCells.push(linkCell(url, [], []));
    }
    if (fullWidthCells.length) {
      rows.push({
        heading: title,
        groupedCells: [],
        fullWidthCells
      });
    }
  }
  return rows;
}
function adaptSectionTable(data: SanityProductsNewData, sectionTitle: string): DownloadRow[] {
  const section = (data.sections ?? []).find(s => (s.title ?? "").toLowerCase() === sectionTitle.toLowerCase());
  if (!section) return [];
  return (section.rows ?? []).map((row): DownloadRow | null => {
    const links = row.links ?? [];
    const fullWidthCells: DownloadCell[] = links.map(l => {
      if (!l.url) return null;
      const label = l.label?.trim() || "Link";
      return linkCell(l.url, [], [], label);
    }).filter((c): c is DownloadCell => Boolean(c));
    if (!fullWidthCells.length) return null;
    return {
      heading: row.name ?? "",
      groupedCells: [],
      fullWidthCells
    };
  }).filter((row): row is DownloadRow => Boolean(row));
}
function adaptHardwareOptions(revs: SanityProductsNewRev[]): DownloadVersionOption[] {
  // Mirror legacy behaviour: `compatibleSlugs` is left empty so every rev is
  // always visible in the hardware picker. Firmware options carry the real
  // per-rev compatibility list, which is what drives the mutual filter.
  // Titles include the sensor list — the dropdown only shows one line, so
  // two "Rev 07" docs (OSDome/OS0/OS1 vs OS2) need the parenthetical here
  // to be distinguishable even though the Studio hides it (auto-derived
  // subtitle handles that).
  return revs.map((rev): DownloadVersionOption | null => {
    if (!rev.slug) return null;
    const sensorNames = (rev.products ?? []).map(p => p.product).filter((n): n is string => Boolean(n));
    const baseTitle = rev.title || rev.slug;
    const title = sensorNames.length ? `${baseTitle} (${sensorNames.join(", ")})` : baseTitle;
    return {
      title,
      slug: rev.slug,
      compatibleSlugs: []
    };
  }).filter((o): o is DownloadVersionOption => Boolean(o));
}
function adaptFirmwareOptions(data: SanityProductsNewData): DownloadVersionOption[] {
  const firmwares = data.firmwares ?? [];
  const revs = data.revs ?? [];
  return firmwares.map((fw): DownloadVersionOption | null => {
    if (!fw.slug) return null;
    const compatible = revs.filter(rev => (rev.firmwares ?? []).some(f => f.slug === fw.slug)).map(rev => rev.slug).filter((s): s is string => Boolean(s));
    return {
      title: fw.title || fw.slug,
      slug: fw.slug,
      compatibleSlugs: compatible
    };
  }).filter((o): o is DownloadVersionOption => Boolean(o));
}
function adaptWarnings(revs: SanityProductsNewRev[]): DownloadWarningView[] {
  const out: DownloadWarningView[] = [];
  for (const rev of revs) {
    const revSlug = rev.slug;
    if (!revSlug) continue;
    for (const warning of rev.firmwareWarnings ?? []) {
      const fwSlug = warning.firmware?.slug;
      const message = warning.message as SanityRichTextValue | undefined;
      if (!fwSlug || !message) continue;
      out.push({
        leftSlug: revSlug,
        operator: "and",
        rightSlug: fwSlug,
        message
      });
    }
  }
  return out;
}

// Drop null entries from a dereferenced list.
function compact<T>(items: (T | null | undefined)[] | null | undefined): T[] {
  return (items ?? []).filter((item): item is T => item != null);
}

/**
 * A reference row left empty in Studio (or pointing at a deleted doc)
 * dereferences to `null` inside the GROQ arrays. Published data rarely has
 * one, but draft-mode renders read half-edited drafts, and a single `null`
 * used to crash the whole page with a 500. Strip them once, up front.
 */
function withoutNullEntries(data: SanityProductsNewData): SanityProductsNewData {
  return {
    ...data,
    revs: compact(data.revs).map(rev => ({
      ...rev,
      firmwares: compact(rev.firmwares),
      firmwareWarnings: compact(rev.firmwareWarnings),
      guides: compact(rev.guides),
      products: compact(rev.products).map(product => ({
        ...product,
        datasheets: compact(product.datasheets),
        hardwareAccessories: compact(product.hardwareAccessories)
      }))
    })),
    firmwares: compact(data.firmwares),
    guides: compact(data.guides),
    accessories: compact(data.accessories),
    sections: compact(data.sections).map(section => ({
      ...section,
      rows: compact(section.rows).map(row => ({
        ...row,
        links: compact(row.links)
      }))
    }))
  };
}
export function adaptProductsNew(rawData: SanityProductsNewData | null | undefined): DownloadsView {
  const data = rawData ? withoutNullEntries(rawData) : rawData;
  const revs = data?.revs ?? [];
  const tables: Record<DownloadTableKey, DownloadRow[]> = {
    hardwareTable: adaptRevs(revs),
    hardwareAccessoriesTable: data ? adaptAccessoriesTable(data) : [],
    firmwareTable: data ? adaptFirmwareTable(data) : [],
    guidesAndResourcesTable: data ? adaptGuidesTable(data) : [],
    developerToolsTable: data ? adaptSectionTable(data, "Developer Tools") : [],
    ousterStudioVisualizerTable: data ? adaptSectionTable(data, "Ouster Studio Visualizer") : [],
    // Velodyne downloads live on their own /downloads/velodyne-downloads page,
    // linked via a Call To Action list section — not part of this table.
    velodyneSensorsTable: []
  };
  const tooltips: DownloadTooltipView[] = [];
  return {
    hardwareOptions: adaptHardwareOptions(revs),
    firmwareOptions: data ? adaptFirmwareOptions(data) : [],
    warnings: adaptWarnings(revs),
    tooltips,
    tables
  };
}